import {
  BodyText,
  Button,
  Card,
  DetailText,
  Flex,
  Heading,
  Strong,
} from '@utilitywarehouse/hearth-react';
import '@utilitywarehouse/hearth-react-icons';

import { Sim } from '@generated/gql/graphql';
import { formatCurrency } from '@utils/format';
import {
  discountedSimTariff,
  findMultiSimDiscount,
  findUnlimitedMaxDiscount,
  getDiscountDurationMonths,
} from '@utils/mobile';
import { convertValueToCurrency, trimPriceDecimals } from '@utils/price';

import { isPortingPac } from '../../utils';
import './styles.scss';

const formatSimTileData = (sim: Sim) => {
  const { selectedTariff, settings } = sim;
  const discounts = selectedTariff.discounts ?? [];
  const { monthlyCost } = selectedTariff;
  const { afterPromoPrice, totalPrice } = discountedSimTariff(selectedTariff);
  const unlimitedMaxPromo = findUnlimitedMaxDiscount(discounts);
  const hasUnlimitedMaxPromo = !!unlimitedMaxPromo;
  const isDiscount = Boolean(
    findMultiSimDiscount(discounts) || hasUnlimitedMaxPromo
  );
  const discountLengthMonths = unlimitedMaxPromo
    ? getDiscountDurationMonths(unlimitedMaxPromo)
    : null;

  // Only a kept (PAC) number is this SIM's number. With STAC the customer
  // cancels their old contract and receives a new number, and `mobileNumber`
  // holds the number being cancelled — so we leave it blank and let the tile
  // fall back to "New".
  const { porting } = settings;
  const keptNumber =
    porting && isPortingPac(porting) ? porting.mobileNumber : '';

  return {
    name: selectedTariff.name,
    price: convertValueToCurrency(totalPrice),
    discountedPrice: isDiscount ? monthlyCost : null,
    afterPromoPrice: convertValueToCurrency(afterPromoPrice),
    number: keptNumber,
    budgetControl: settings.budgetControl,
    hasUnlimitedMaxPromo,
    discountLengthMonths,
  };
};

interface SimTileProps {
  sim: Sim;
  onEdit: () => void;
  onDelete: () => void;
}

interface ItemProps {
  label: string;
  value: string;
}

const Item = ({ label, value }: ItemProps) => (
  <li className="simTile__list-item">
    <BodyText size="md">{label}: </BodyText>
    <Strong>{value}</Strong>
  </li>
);

const SimTile = ({ sim, onEdit, onDelete }: SimTileProps) => {
  const {
    name,
    price,
    discountedPrice,
    afterPromoPrice,
    number,
    budgetControl,
    hasUnlimitedMaxPromo,
    discountLengthMonths,
  } = formatSimTileData(sim);

  return (
    <Card
      data-testid="mobile-sim-selected"
      spacing="lg"
      direction="column"
      flex="1"
    >
      <Heading size="md">{name}</Heading>
      <Flex gap="100" alignItems="baseline" wrap="wrap">
        {discountedPrice && (
          <DetailText size="md" className="simTile__discounted-price">
            {trimPriceDecimals(formatCurrency(discountedPrice))}
          </DetailText>
        )}
        <DetailText
          size="3xl"
          color={hasUnlimitedMaxPromo ? 'valid' : undefined}
        >
          {formatCurrency(price)}
          {hasUnlimitedMaxPromo && <sup style={{ lineHeight: 0 }}>*</sup>}
        </DetailText>{' '}
        {!hasUnlimitedMaxPromo && <BodyText size="sm">a month</BodyText>}
        {hasUnlimitedMaxPromo && discountLengthMonths && (
          <BodyText className="simTile__promo-note" size="sm">
            for {discountLengthMonths} months, then{' '}
            <Strong>{formatCurrency(afterPromoPrice)}</Strong> a month
          </BodyText>
        )}
      </Flex>

      <ul className="simTile__list" data-testid="mobile-sim-selected-details">
        <Item label="Number" value={number || 'New'} />
        <Item label="Budget control" value={budgetControl ? 'On' : 'Off'} />
      </ul>
      <Flex spacing="lg" marginTop="auto">
        <Button size="sm" variant="ghost" paddingNone onClick={onEdit}>
          Edit
        </Button>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="destructive"
          paddingNone
          onClick={onDelete}
        >
          Remove
        </Button>
      </Flex>
    </Card>
  );
};

export { SimTile };
